/**
 * Verify Engine load-balancer routing hint.
 *
 * Production ingress hashes `/api/websocket` connections by the `sid` query
 * parameter so that every connection (and every reconnect) of one verification
 * session reaches the same Engine pod, while different sessions spread across
 * pods. The Engine never trusts this value: it authenticates the session from
 * the `start_session` message (`session_id` + `session_token`). The parameter
 * is therefore only a routing hint and must never carry a credential.
 *
 * Only a Portal session UUID is ever placed in the URL. Anything else is
 * omitted, which routes the connection exactly as before this hint existed.
 */
export const ENGINE_ROUTING_QUERY_PARAMETER = 'sid';

const SESSION_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isRoutableSessionId(sessionId: unknown): sessionId is string {
  return typeof sessionId === 'string' && SESSION_UUID.test(sessionId);
}

/**
 * Return `wsUrl` carrying the per-session routing key. The result depends only
 * on its inputs, so reconnects for the same session produce the same URL.
 */
export function withEngineRoutingKey(wsUrl: string, sessionId?: string | null): string {
  if (!isRoutableSessionId(sessionId)) {
    return wsUrl;
  }
  let url: URL;
  try {
    url = new URL(wsUrl);
  } catch {
    return wsUrl;
  }
  url.searchParams.set(ENGINE_ROUTING_QUERY_PARAMETER, sessionId);
  return url.toString();
}
